import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Star } from "lucide-react";
import { EmailDeliverabilityCard } from "@/components/EmailDeliverabilityCard";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }) : "—");
const DEST = [
  ["judgeme", "Judge.me on the product page", "Default · the vehicle's page on leprecon.plus + #judgeme (found from the Shopify order, else by brand + model)"],
  ["google", "Google review", "Paste your Google Business review link (g.page/r/…/review)"],
  ["facebook", "Facebook review", "Paste your Facebook page reviews link"],
  ["custom", "Other link", "Any other review page (Trustpilot, Yelp, …)"],
];
const NUM = ["first_delay_days", "reminder_interval_days", "max_reminders", "lookback_days"];
const TXT = ["store_url", "google_review_url", "facebook_review_url", "custom_review_url", "incentive_text", "reply_to", "photos_notify_email"];

// Settings card: ⭐ photo / review request schedule, review destination switch, links, counts, run-now.
export function ReviewRequestsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["review-requests-settings"], queryFn: async () => (await api.get("/review-requests/settings")).data });
  const [draft, setDraft] = useState(null);
  useEffect(() => { if (data?.settings && draft === null) setDraft(data.settings); }, [data, draft]);
  const save = useMutation({
    mutationFn: async (patch) => (await api.put("/review-requests/settings", patch)).data,
    onSuccess: (r) => { qc.setQueryData(["review-requests-settings"], (old) => ({ ...(old || {}), settings: r.settings, overview: r.overview })); setDraft(r.settings); toast.success("Review request settings saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });
  const run = useMutation({
    mutationFn: async () => (await api.post("/review-requests/run")).data,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["review-requests-settings"] }); toast.success(`Scheduler ran · first ${r.stats.first} · reminders ${r.stats.reminders} · capped ${r.stats.capped} · skipped ${r.stats.skipped}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not run"),
  });
  const relinkQ = useQuery({
    queryKey: ["review-relink"],
    queryFn: async () => (await api.get("/review-requests/relink")).data,
    refetchInterval: (q) => (q.state.data?.running ? 2000 : false),
  });
  const relink = useMutation({
    mutationFn: async () => (await api.post("/review-requests/relink")).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["review-relink"] }); toast.success("Re-linking product pages — this runs in the background"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start"),
  });
  const rl = relinkQ.data || {};
  const st = data?.settings || {};
  const ov = data?.overview || {};
  const d = draft || st;
  const dirty = draft && [...NUM, ...TXT, "review_destination", "skip_open_claims", "stop_on_form"].some((k) => String(draft[k] ?? "") !== String(st[k] ?? ""));
  const patch = (k, v) => setDraft((p) => ({ ...(p || st), [k]: v }));
  const submit = () => {
    const body = {};
    NUM.forEach((k) => { body[k] = Number(d[k]); });
    ["store_url", "incentive_text", "reply_to", "photos_notify_email"].forEach((k) => { body[k] = d[k] ?? ""; });
    if (d.review_destination !== "judgeme") body[`${d.review_destination}_review_url`] = d[`${d.review_destination}_review_url`] ?? "";
    body.review_destination = d.review_destination; body.skip_open_claims = !!d.skip_open_claims; body.stop_on_form = !!d.stop_on_form;
    save.mutate(body);
  };
  const c = ov.counts || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="review-requests-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Star size={22} className="text-amber-500" />
        <h2 className="font-semibold text-lg">Photo / review requests</h2>
        {data && (
          <Badge className={st.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-700 border border-slate-200"} data-testid="review-requests-status">
            {st.enabled ? "Automatic · ON" : "Automatic · OFF"}
          </Badge>
        )}
        <span className="ml-auto flex items-center gap-2 text-sm">
          <Label htmlFor="rr-enabled" className="text-xs text-slate-600">Auto-send</Label>
          <Switch id="rr-enabled" checked={!!st.enabled} disabled={save.isPending || isLoading} onCheckedChange={(on) => save.mutate({ enabled: on })} data-testid="review-requests-enabled-switch" />
        </span>
      </div>
      <p className="text-sm text-slate-600 mt-2">
        <b>{d.first_delay_days ?? 21} days</b> after an order is fulfilled the customer gets the <i>Photo Review</i> e-mail (template editable below), then a reminder every <b>{d.reminder_interval_days ?? 14} days</b> (max {d.max_reminders ?? 3}) until they click the review link, unsubscribe, or answer the installer / photo form. The ⭐ button on a fulfilled order sends it at once. Replies go to <b>{d.reply_to || "shop@leprecon.ca"}</b>. When the customer clicks the review link or shares photos, the order's <b>FIT is signed by the customer</b> (green FIT ✓ sign, Approvals &amp; Sign-offs, CAT # record), every photo is <b>saved to the order's Drive folder</b>, and a short <b>"client responded"</b> notification goes to <b>{d.photos_notify_email || "shop@leprecon.plus"}</b>.
        {st.enabled && st.enabled_at && <> Switched on {fmt(st.enabled_at)} by {st.enabled_by} — orders fulfilled up to {d.lookback_days} days before that are included.</>}
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Where the ⭐ button sends the customer</div>
          <div className="space-y-2" data-testid="review-destination-options">
            {DEST.map(([k, label, hint]) => (
              <label key={k} className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${d.review_destination === k ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`review-destination-${k}`}>
                <input type="radio" name="review_destination" className="mt-1" checked={d.review_destination === k} onChange={() => patch("review_destination", k)} />
                <span className="flex-1">
                  <span className="text-sm font-semibold">{label}</span>
                  <span className="block text-xs text-slate-500">{hint}</span>
                  {k !== "judgeme" && d.review_destination === k && (
                    <Input value={d[`${k}_review_url`] || ""} onChange={(e) => patch(`${k}_review_url`, e.target.value)} placeholder="https://…" className="mt-2 h-9 text-sm" data-testid={`review-${k}-url`} />
                  )}
                </span>
              </label>
            ))}
          </div>
          <div>
            <Label className="text-xs">Store URL (product pages)</Label>
            <Input value={d.store_url || ""} onChange={(e) => patch("store_url", e.target.value)} className="h-9 text-sm" data-testid="review-store-url" />
          </div>
        </div>
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Schedule</div>
          <div className="grid grid-cols-2 gap-3">
            {[["first_delay_days", "First e-mail · days after fulfilled"], ["reminder_interval_days", "Reminder every · days"], ["max_reminders", "Max reminders after the first"], ["lookback_days", "Include orders fulfilled up to · days before ON"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs">{l}</Label>
                <Input type="number" min={0} value={d[k] ?? ""} onChange={(e) => patch(k, e.target.value)} className="h-9 text-sm" data-testid={`review-${k.replace(/_/g, "-")}`} />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Reply-to address</Label>
            <Input value={d.reply_to || ""} onChange={(e) => patch("reply_to", e.target.value)} className="h-9 text-sm" data-testid="review-reply-to" />
          </div>
          <div>
            <Label className="text-xs">"Client responded" notification goes to <span className="text-slate-400">(short e-mail, no attachments — photos are saved to the order's Drive folder)</span></Label>
            <Input value={d.photos_notify_email || ""} onChange={(e) => patch("photos_notify_email", e.target.value)} className="h-9 text-sm" data-testid="review-photos-notify-email" />
          </div>
          <div>
            <Label className="text-xs">Incentive line (optional · shown under the button)</Label>
            <Input value={d.incentive_text || ""} onChange={(e) => patch("incentive_text", e.target.value)} placeholder="e.g. Post a photo review and get 10% off your next set with code REVIEW10" className="h-9 text-sm" data-testid="review-incentive" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.skip_open_claims} onChange={(e) => patch("skip_open_claims", e.target.checked)} data-testid="review-skip-claims" /> Don't ask while a claim is open on the order</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.stop_on_form} onChange={(e) => patch("stop_on_form", e.target.checked)} data-testid="review-stop-on-form" /> Stop reminders once the customer answers the installer / photo form</label>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={!dirty || save.isPending} onClick={submit} data-testid="review-settings-save">{save.isPending ? "Saving…" : "Save settings"}</Button>
        {dirty && <Button size="sm" variant="ghost" onClick={() => setDraft(st)} data-testid="review-settings-discard">Discard</Button>}
        <Button size="sm" variant="outline" disabled={run.isPending || !st.enabled} onClick={() => run.mutate()} data-testid="review-run-now" title="Send every first e-mail / reminder that is due right now (the scheduler also runs hourly)">{run.isPending ? "Running…" : "Run scheduler now"}</Button>
        {ov.last_run_at && <span className="text-xs text-slate-500">Last run {fmt(ov.last_run_at)}</span>}
      </div>

      <div className="mt-4 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3" data-testid="review-relink-box">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm">
            <b>Product page links</b> — the ⭐ link must open the vehicle's own page (e.g. <span className="font-mono text-xs">/products/lexus-is-custom-caliper-covers#judgeme</span>), never the store search page.
          </div>
          <Button size="sm" variant="outline" className="ml-auto border-amber-500 text-amber-900 hover:bg-amber-100" disabled={relink.isPending || rl.running} onClick={() => relink.mutate()} data-testid="review-relink-btn"
                  title="Re-check every fulfilled order: Shopify order → product page, else brand + model → the store's vehicle page. Orders where the customer already replied, and manually set links, are left as they are.">
            {rl.running ? "Re-linking…" : "Re-link product pages"}
          </Button>
        </div>
        {(rl.started_at || rl.error) && (
          <div className="mt-1 text-xs text-slate-700" data-testid="review-relink-status">
            {rl.running ? `Running since ${fmt(rl.started_at)} · ${rl.total ?? 0} checked · ${rl.changed ?? 0} changed` :
              rl.error ? <span className="text-rose-700">Last attempt {fmt(rl.started_at)} failed: {rl.error}</span> :
              <>Last run {fmt(rl.done_at)} by {rl.by} · {rl.total ?? 0} orders checked · <b>{rl.changed ?? 0} re-linked</b> · {rl.unchanged ?? 0} already right · {rl.kept_replied ?? 0} kept (customer replied) · {rl.kept_manual ?? 0} kept (manual) · {rl.still_search ?? 0} no product page found · catalogue {rl.catalogue ?? 0} products</>}
          </div>
        )}
        {!rl.running && (rl.samples || []).length > 0 && (
          <details className="mt-1 text-xs">
            <summary className="cursor-pointer text-slate-600">Show what changed ({rl.samples.length}{rl.changed > rl.samples.length ? ` of ${rl.changed}` : ""})</summary>
            <ul className="mt-1 space-y-0.5 font-mono" data-testid="review-relink-samples">
              {rl.samples.map((s, i) => <li key={i}>{s.po_number} · {s.vehicle} → {String(s.to || "").replace(/^https?:\/\/[^/]+/, "")} <span className="text-slate-400">({s.source})</span></li>)}
            </ul>
          </details>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-center" data-testid="review-overview">
        {[["Waiting (not due)", ov.waiting], ["Due now", ov.due_now], ["Cycle running", c.sent], ["Reminders due", ov.reminders_due], ["Clicked ✓", c.clicked], ["Form answered", c.form], ["Unsubscribed", c.unsubscribed], ["Photos shared", ov.photos]].map(([l, v]) => (
          <div key={l} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2">
            <div className="text-lg font-bold tabular-nums">{v ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-tight">{l}</div>
          </div>
        ))}
      </div>
      {(ov.recent || []).length > 0 && (
        <div className="mt-4 rounded-md border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs" data-testid="review-recent-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2 text-left">Order</th><th className="px-3 py-2 text-left">Vehicle</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Sent</th><th className="px-3 py-2 text-left">Next</th><th className="px-3 py-2 text-left">Photos</th><th className="px-3 py-2 text-left">Installed by</th></tr>
            </thead>
            <tbody>
              {ov.recent.slice(0, 15).map((r) => (
                <tr key={r.id} className="border-t" data-testid={`review-recent-row-${r.id}`}>
                  <td className="px-3 py-1.5 whitespace-nowrap"><a href={`/order/${r.id}`} className="underline text-sky-700">{r.po_number}</a></td>
                  <td className="px-3 py-1.5">{r.vehicle}</td>
                  <td className="px-3 py-1.5" title={r.reason || ""}>{r.status}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">×{r.send_count} · {fmt(r.last_sent_at)}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.next_at ? fmt(r.next_at) : "—"}</td>
                  <td className="px-3 py-1.5">{r.photos || 0}</td>
                  <td className="px-3 py-1.5">{r.installed_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EmailDeliverabilityCard />
    </div>
  );
}
